import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rolePermissionService } from '../services/api';
import { RolePermission, User } from '../types';

type ConfigurableRole = Exclude<User['role'], 'owner'>;
type RoleMatrix = Record<User['role'], RolePermission[]>;

const ROLES: User['role'][] = ['owner', 'admin', 'staff', 'member', 'robot'];
const EMPTY_MATRIX: RoleMatrix = { owner: [], admin: [], staff: [], member: [], robot: [] };

function samePermissions(left: RolePermission[], right: RolePermission[]) {
  return [...left].sort().join(',') === [...right].sort().join(',');
}

const ManageRolePermissionsPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [matrix, setMatrix] = useState<RoleMatrix>(EMPTY_MATRIX);
  const [savedMatrix, setSavedMatrix] = useState<RoleMatrix>(EMPTY_MATRIX);
  const [definitions, setDefinitions] = useState<Array<{ key: RolePermission; label: string; group: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirtyRoles = useMemo(
    () => ROLES.filter((role) => role !== 'owner' && !samePermissions(matrix[role], savedMatrix[role])) as ConfigurableRole[],
    [matrix, savedMatrix],
  );

  useEffect(() => {
    if (user?.role !== 'owner') {
      setLoading(false);
      return;
    }

    rolePermissionService.list()
      .then((response) => {
        const next: RoleMatrix = { ...EMPTY_MATRIX };
        response.data.roles.forEach(({ role, permissions }) => {
          next[role] = permissions;
        });
        setMatrix(next);
        setSavedMatrix(next);
        setDefinitions(response.data.definitions);
      })
      .catch(() => setError('Failed to load role permissions'))
      .finally(() => setLoading(false));
  }, [user?.role]);

  const togglePermission = (role: ConfigurableRole, permission: RolePermission) => {
    setSaved(false);
    setMatrix((current) => {
      const assigned = current[role].includes(permission);
      return {
        ...current,
        [role]: assigned
          ? current[role].filter((currentPermission) => currentPermission !== permission)
          : [...current[role], permission],
      };
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updates = await Promise.all(dirtyRoles.map((role) => rolePermissionService.update(role, matrix[role])));
      setMatrix((current) => {
        const next = { ...current };
        updates.forEach(({ data }) => {
          next[data.role as User['role']] = data.permissions;
        });
        return next;
      });
      setSavedMatrix((current) => {
        const next = { ...current };
        updates.forEach(({ data }) => {
          next[data.role as User['role']] = data.permissions;
        });
        return next;
      });
      setSaved(true);
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to save role permissions');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'owner') return <Navigate to="/access-denied" replace />;

  const permissionGroups = Array.from(new Set(definitions.map(({ group }) => group)));

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Owner controls</p>
            <h1 className="page-heading">Role permissions</h1>
            <p className="page-copy">Configure what each role can access across discussions, operations, and administration.</p>
          </div>
          <div className="hero-actions">
            <button type="button" className="btn btn-primary" onClick={saveChanges} disabled={loading || saving || dirtyRoles.length === 0}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {saved && <div className="alert alert-success">Role permissions saved.</div>}
        <p className="field-help">Owner access is fixed so the dashboard always has an administrator. Robot accounts cannot review or publish discussions; another authorized role must approve Robot-authored products.</p>

        {loading ? (
          <div className="card">Loading role permissions...</div>
        ) : (
          <div className="card users-table-card">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  {ROLES.map((role) => <th key={role}>{role}</th>)}
                </tr>
              </thead>
              <tbody>
                {permissionGroups.map((group) => (
                  <React.Fragment key={group}>
                    <tr><th colSpan={ROLES.length + 1}>{group}</th></tr>
                    {definitions.filter((definition) => definition.group === group).map((definition) => (
                      <tr key={definition.key}>
                        <td>{definition.label}</td>
                        {ROLES.map((role) => {
                          const fixed = role === 'owner' || (role === 'robot' && ['review_discussions', 'publish_discussions'].includes(definition.key));
                          return (
                            <td key={`${role}-${definition.key}`}>
                              <input
                                type="checkbox"
                                aria-label={`${definition.label} for ${role}`}
                                checked={matrix[role].includes(definition.key)}
                                disabled={fixed || saving}
                                onChange={() => togglePermission(role as ConfigurableRole, definition.key)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageRolePermissionsPage;
